// src/components/LanguageSwitcher.jsx - Sélecteur de langue sans drapeaux
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check, ChevronDown } from 'lucide-react';

const LanguageSwitcher = ({ className = '', variant = 'default' }) => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const languages = [
    { code: 'fr', name: 'Français', dir: 'ltr' },
    { code: 'en', name: 'English', dir: 'ltr' },
    { code: 'es', name: 'Español', dir: 'ltr' },
    { code: 'ar', name: 'العربية', dir: 'rtl' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const changeLanguage = (langCode) => {
    i18n.changeLanguage(langCode);
    
    // Mettre à jour la direction du document pour l'arabe
    const newLang = languages.find(lang => lang.code === langCode);
    if (newLang) {
      document.documentElement.dir = newLang.dir;
      document.documentElement.lang = langCode;
    }
    
    setIsOpen(false);
  };

  // Style compact pour navigation - VERSION AGRANDIE
  if (variant === 'compact') {
    return (
      <div className={`relative ${className}`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center px-5 py-3 rounded-lg hover:bg-gray-100 transition-colors"
          title={t('common.changeLanguage', 'Changer de langue')}
        >
          <Globe className="w-6 h-6 text-gray-600" />
          <span className="ml-3 text-lg font-medium text-gray-700">{currentLanguage.code.toUpperCase()}</span>
          <ChevronDown className="w-4 h-4 ml-2 text-gray-500" />
        </button>

        {isOpen && (
          <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-3 min-w-[200px] z-50">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className="w-full px-5 py-3 text-left hover:bg-gray-50 flex items-center justify-between"
              >
                <div className="flex items-center space-x-4">
                  <span className="text-base font-medium text-gray-700">{lang.name}</span>
                </div>
                {i18n.language === lang.code && (
                  <Check className="w-5 h-5 text-green-500" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Style moderne - VERSION AGRANDIE
  if (variant === 'modern') {
    return (
      <div className={`relative ${className}`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center px-6 py-3 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full hover:bg-white hover:shadow-md transition-all"
        >
          <Globe className="w-5 h-5 text-gray-600 mr-3" />
          <span className="text-base font-medium text-gray-700 mr-2">{currentLanguage.code.toUpperCase()}</span>
          <ChevronDown className="w-4 h-4 text-gray-500" />
        </button>

        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-40" 
              onClick={() => setIsOpen(false)}
            />
            <div className="absolute right-0 top-full mt-2 bg-white rounded-xl shadow-xl border border-gray-200 py-4 min-w-[260px] z-50 overflow-hidden">
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => changeLanguage(lang.code)}
                  className={`w-full px-6 py-4 text-left hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all group ${
                    i18n.language === lang.code ? 'bg-gradient-to-r from-blue-50 to-purple-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="text-left">
                        <div className="text-base font-medium text-gray-800">{lang.name}</div>
                        <div className="text-sm text-gray-500">{lang.code.toUpperCase()}</div>
                      </div>
                    </div>
                    {i18n.language === lang.code && (
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                        <Check className="w-5 h-5 text-green-500" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
              <div className="mt-3 px-6 py-3 border-t border-gray-100">
                <p className="text-sm text-gray-400 text-center">
                  {t('common.languageSwitch', 'Interface language')}
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Style complet par défaut - VERSION AGRANDIE
  return (
    <div className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center px-6 py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
      >
        <Globe className="w-6 h-6 text-gray-600 mr-3" />
        <span className="text-base font-medium text-gray-700 mr-3">{currentLanguage.name}</span>
        <ChevronDown className="w-5 h-5 text-gray-500" />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl border border-gray-200 py-3 min-w-[240px] z-50">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => changeLanguage(lang.code)}
                className="w-full px-6 py-4 text-left hover:bg-gray-50 flex items-center justify-between transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <span className="text-base font-medium text-gray-700">{lang.name}</span>
                </div>
                {i18n.language === lang.code && (
                  <Check className="w-5 h-5 text-green-500" />
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSwitcher;